import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import ContentCard from '@/components/base/ContentCard';
import Reveal from '@/components/base/Reveal';
import { useSeo } from '@/hooks/useSeo';
import { contents } from '@/mocks/contents';
import { categories } from '@/mocks/home';
import { categoryNameMap, SITE_ORIGIN, SITE_NAME, sortOptions } from '@/lib/site';
import { btn } from '@/lib/ui';

type Overrides = { category?: string; sort?: string; q?: string };

const Category = () => {
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get('category') || 'all';
  const query = params.get('q') || '';
  const sort = params.get('sort') || 'hot';
  const [keyword, setKeyword] = useState(query);

  const activeName = categoryNameMap[activeCategory] || '全部';

  const list = useMemo(() => {
    let result = [...contents];
    if (activeCategory !== 'all') {
      result = result.filter((item) => item.category === activeCategory);
    }
    const kw = query.trim().toLowerCase();
    if (kw) {
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(kw) ||
          item.summary.toLowerCase().includes(kw) ||
          item.categoryName.includes(kw) ||
          item.tags.some((tag) => tag.toLowerCase().includes(kw)),
      );
    }
    if (sort === 'new') result.sort((a, b) => b.date.localeCompare(a.date));
    else if (sort === 'duration') result.sort((a, b) => a.duration.localeCompare(b.duration));
    else result.sort((a, b) => b.hot - a.hot);
    return result;
  }, [activeCategory, query, sort]);

  const buildHref = (overrides: Overrides = {}) => {
    const next = new URLSearchParams();
    const cat = overrides.category ?? activeCategory;
    const s = overrides.sort ?? sort;
    const qv = overrides.q ?? query;
    if (cat && cat !== 'all') next.set('category', cat);
    if (s && s !== 'hot') next.set('sort', s);
    if (qv) next.set('q', qv);
    const qs = next.toString();
    return qs ? `/category?${qs}` : '/category';
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(params);
    if (keyword.trim()) next.set('q', keyword.trim());
    else next.delete('q');
    setParams(next);
  };

  const title =
    activeCategory === 'all'
      ? '内容分类 - 在线看料热门现场与话题一次看完'
      : `${activeName}在线看料 - 高清不卡顿的${activeName}内容合集`;

  useSeo({
    title: `${title} - ${SITE_NAME}`,
    description: `看料社${activeName}栏目，聚合${activeName}相关的热门现场与内容解析，在线看料高清不卡顿秒开，超清画质加载快，支持按热度、最新与时长排序筛选。`,
    keywords: `在线看料,${activeName},在线看料高清,内容分类,看料社`,
    path: '/category',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${activeName}在线看料`,
        url: `${SITE_ORIGIN}/category`,
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_ORIGIN },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_ORIGIN}/` },
          { '@type': 'ListItem', position: 2, name: '内容分类', item: `${SITE_ORIGIN}/category` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        numberOfItems: list.length,
        itemListElement: list.slice(0, 10).map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.title,
          url: `${SITE_ORIGIN}/content/${item.id}`,
        })),
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100 pt-24 md:pt-28">
        <div className="mx-auto w-full max-w-shell px-4 py-10 md:px-8 md:py-14">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs font-label text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="text-foreground-800">内容分类</span>
            {activeCategory !== 'all' && (
              <>
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-s-line text-sm" />
                </span>
                <span className="text-foreground-800">{activeName}</span>
              </>
            )}
          </nav>

          <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-4xl">
            {activeCategory === 'all' ? '全部内容' : activeName}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            共 {list.length} 条内容，按{sortOptions.find((option) => option.id === sort)?.label || '最热'}
            排序。支持按分类切换、关键词搜索，找到你想看的那一条。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-shell px-4 py-10 md:px-8 md:py-14">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex gap-1 overflow-x-auto rounded-full border border-background-200 bg-background-50 p-1" role="tablist" aria-label="分类切换">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={buildHref({ category: category.id })}
                role="tab"
                aria-selected={activeCategory === category.id}
                className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-xs font-label transition-colors md:text-sm ${
                  activeCategory === category.id
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={`${category.icon} text-sm`} />
                </span>
                {category.name}
              </Link>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <form onSubmit={submitSearch} className="relative w-full sm:w-64" role="search">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center">
                <i className="ri-search-line text-sm text-foreground-400" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索标题、标签或分类"
                aria-label="搜索内容"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </form>
            <div className="flex gap-1 rounded-full border border-background-200 bg-background-50 p-1">
              {sortOptions.map((option) => (
                <Link
                  key={option.id}
                  to={buildHref({ sort: option.id })}
                  className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-label transition-colors ${
                    sort === option.id
                      ? 'bg-secondary-500 text-background-50 dark:text-foreground-950'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className={`${option.icon} text-sm`} />
                  </span>
                  {option.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {list.length > 0 ? (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
            {list.map((item, index) => (
              <Reveal key={item.id} delay={(index % 3) * 60}>
                <ContentCard item={item} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-12 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
              没有找到匹配的内容
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              试试更换关键词，或切换到其他分类看看。你也可以直接浏览全部内容。
            </p>
            <Link to="/category" className={`${btn('primary', 'md')} mt-6`}>
              查看全部内容
            </Link>
          </div>
        )}
      </div>
    </SiteLayout>
  );
};

export default Category;